/**
 * Espelho no front da classificação de rejeições de NFS-e do backend
 * (`modules/nfse/domain/nfse-rejection.ts`). Lê o `errorPayload` enriquecido
 * (`rejectionKind`/`code`/`friendlyMessage`) e, como fallback para registros
 * antigos, reclassifica por `code`/`message`.
 *
 * Rejeições de autorização (E347 — prestador não credenciado) são definitivas:
 * a UI mostra orientação de credenciamento e não oferece retry.
 */

export type RejectionKind = 'AUTHORIZATION' | 'FIELD' | 'UNKNOWN';

export interface RejectionDisplay {
  kind: RejectionKind;
  title: string;
  hint: string;
}

const AUTHORIZATION_HINT =
  'O CNPJ ainda não está autorizado a emitir NFS-e neste município. Solicite o ' +
  'credenciamento do prestador na prefeitura ou adira ao Emissor Nacional de NFS-e ' +
  '(gov.br/nfse), informando CNPJ e Inscrição Municipal. Depois, tente novamente.';

const AUTHORIZATION_MESSAGE_RE =
  /n[ãa]o\s+autorizad.\s+a\s+emitir|prestador.*n[ãa]o\s+autorizad|credenciament/i;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Deriva o bloco de mensagem amigável para um `errorPayload` de emissão
 * rejeitada/falha. Retorna `null` quando não há nada útil a exibir.
 */
export function friendlyRejectionMessage(errorPayload: unknown): RejectionDisplay | null {
  const payload = asRecord(errorPayload);
  if (!payload) return null;

  const code = str(payload.code)?.toUpperCase() ?? null;
  const message = str(payload.message) ?? str(payload.friendlyMessage) ?? '';

  const kind: RejectionKind =
    (payload.rejectionKind as RejectionKind | undefined) ??
    (code === 'E347' || AUTHORIZATION_MESSAGE_RE.test(message) ? 'AUTHORIZATION' : 'UNKNOWN');

  if (kind === 'AUTHORIZATION') {
    return {
      kind,
      title: 'Prestador não autorizado a emitir NFS-e',
      hint: str(payload.friendlyMessage) ?? AUTHORIZATION_HINT,
    };
  }

  const hint = str(payload.friendlyMessage) ?? message;
  if (!hint) return null;
  return { kind, title: 'Emissão rejeitada', hint };
}
